import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { Heart, Check, X, Mail, AlertTriangle, ArrowLeft } from "lucide-react";
import { RenderStructuredValue } from "../formatProfil";

// Section à intégrer dans une page Sécurité ou un onglet dédié
export function CoupleSection() {
  const [state, setState] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/couple/me");
      setState(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/couple/invite", { email });
      setEmail("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Confirmer la révocation de la liaison conjoint ? Cette action est irréversible.")) return;
    setError("");
    try {
      await api.post("/couple/revoke");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (loading) {
    return <div className="auprea-card"><div style={{ color: "rgba(35,38,46,0.5)", textAlign: "center" }}>Chargement…</div></div>;
  }

  return (
    <div className="auprea-card" data-testid="couple-section">
      <div className="flex items-center gap-3 mb-2">
        <Heart size={20} style={{ color: "var(--auprea-gold)" }} />
        <h2 style={{ fontSize: 22, margin: 0 }}>Carnet partagé conjoint</h2>
      </div>
      <p style={{ fontSize: 14, color: "rgba(35,38,46,0.65)", marginBottom: 20, lineHeight: 1.6 }}>
        Reliez votre carnet à celui de votre conjoint(e). Une fois la liaison établie, vous pourrez
        consulter mutuellement vos carnets en <strong>lecture seule</strong>. Révocable à tout moment.
      </p>

      {error && (
        <div style={{ color: "#b53a3a", fontSize: 14, padding: "10px 12px", background: "#fcf3f3", border: "1px solid #f0c5c5", marginBottom: 16 }} data-testid="couple-error">
          {error}
        </div>
      )}

      {state?.status === "none" && (
        <form onSubmit={invite}>
          <label className="auprea-label">Email de votre conjoint(e)</label>
          <div className="flex gap-2">
            <input type="email" className="auprea-input flex-1" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="conjoint@email.com" data-testid="couple-invite-email" />
            <button type="submit" className="auprea-btn" disabled={submitting} data-testid="couple-invite-submit">
              {submitting ? "Envoi…" : "Inviter"}
            </button>
          </div>
        </form>
      )}

      {state?.status === "invited" && (
        <div style={{ background: "rgba(202,168,106,0.08)", border: "1px solid rgba(202,168,106,0.3)", padding: 18, borderRadius: 4 }} data-testid="couple-pending">
          <div className="flex items-center gap-3 mb-2">
            <Mail size={18} style={{ color: "var(--auprea-gold)" }} />
            <strong style={{ color: "var(--auprea-navy)" }}>Invitation envoyée</strong>
          </div>
          <p style={{ fontSize: 14, color: "rgba(35,38,46,0.75)", margin: "6px 0 12px 0" }}>
            En attente d'acceptation par <strong>{state.invited_email}</strong>.
          </p>
          <button onClick={revoke} style={{ fontSize: 13, color: "#b53a3a", textDecoration: "underline" }} data-testid="couple-cancel-invite">
            Annuler l'invitation
          </button>
        </div>
      )}

      {state?.status === "active" && (
        <div style={{ background: "rgba(47,125,91,0.06)", border: "1px solid rgba(47,125,91,0.3)", padding: 20, borderRadius: 4 }} data-testid="couple-active">
          <div className="flex items-center gap-3 mb-3">
            <Check size={18} style={{ color: "var(--auprea-green)" }} />
            <strong style={{ color: "var(--auprea-navy)" }}>Liaison active</strong>
          </div>
          <p style={{ fontSize: 14, color: "rgba(35,38,46,0.85)", margin: "0 0 18px 0" }}>
            Vous êtes relié(e) à <strong>{state.partner.full_name || state.partner.email}</strong>
            <span style={{ color: "rgba(35,38,46,0.55)", fontSize: 13 }}> · depuis le {new Date(state.since).toLocaleDateString("fr-FR")}</span>
          </p>

          {/* Carnet du foyer - indicateur visuel */}
          <div style={{ background: "#fff", padding: 18, borderRadius: 6, border: "1px solid rgba(21,41,79,0.08)", marginBottom: 16 }} data-testid="foyer-progression">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600 }}>— Carnet du foyer</div>
                <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginTop: 2 }}>Avancement consolidé</div>
              </div>
              <div style={{ fontFamily: "Spectral, serif", fontSize: 36, color: "var(--auprea-navy)", lineHeight: 1, fontWeight: 500 }} data-testid="foyer-progression-value">
                {state.foyer_progression || 0}%
              </div>
            </div>
            <div style={{ height: 10, background: "rgba(21,41,79,0.06)", borderRadius: 999, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ width: `${state.foyer_progression || 0}%`, height: "100%", background: "linear-gradient(90deg, var(--auprea-navy), var(--auprea-gold))", transition: "width 0.6s ease" }} />
            </div>

            {/* Détail des 2 carnets */}
            <ProgressRow label="Mon carnet" value={state.my_progression || 0} accent="navy" testid="mon-progression" />
            <div style={{ height: 10 }} />
            <ProgressRow label={state.partner.full_name?.split(" ")[0] || "Conjoint"} value={state.partner_progression || 0} accent="gold" testid="partner-progression" />

            {state.foyer_progression < 100 && (
              <div style={{ marginTop: 16, padding: 12, background: "rgba(202,168,106,0.08)", fontSize: 13, color: "rgba(35,38,46,0.75)", borderRadius: 4, lineHeight: 1.5 }}>
                💡 {state.my_progression < state.partner_progression
                  ? `Votre conjoint a pris de l'avance sur le carnet. Mettez-vous au niveau pour atteindre 100% à deux !`
                  : state.partner_progression < state.my_progression
                  ? `Vous avez ${state.my_progression - state.partner_progression} points d'avance. Encouragez votre conjoint à compléter le sien.`
                  : `Beau parcours en duo ! Continuez ensemble jusqu'à 100%.`}
              </div>
            )}
            {state.foyer_progression === 100 && (
              <div style={{ marginTop: 16, padding: 12, background: "rgba(47,125,91,0.08)", fontSize: 13, color: "var(--auprea-green)", borderRadius: 4, fontWeight: 600, textAlign: "center" }}>
                ⭐ Carnet du foyer complet à 100% — bravo à vous deux !
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-3 flex-wrap">
            <Link to="/conjoint" className="auprea-btn" style={{ background: "var(--auprea-navy)" }} data-testid="couple-view-carnet">
              Voir le carnet de mon conjoint
            </Link>
            <button onClick={revoke} className="auprea-btn" style={{ background: "#fff", color: "#b53a3a", border: "1px solid #b53a3a" }} data-testid="couple-revoke">
              Révoquer la liaison
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressRow({ label, value, accent, testid }) {
  const color = accent === "navy" ? "var(--auprea-navy)" : "var(--auprea-gold)";
  return (
    <div data-testid={testid}>
      <div className="flex justify-between" style={{ fontSize: 13, marginBottom: 4 }}>
        <span style={{ color: "var(--auprea-ink)", fontWeight: 500 }}>{label}</span>
        <span style={{ color: "rgba(35,38,46,0.7)", fontWeight: 600 }}>{value}%</span>
      </div>
      <div style={{ height: 6, background: "rgba(21,41,79,0.06)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${value}%`, height: "100%", background: color, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

// Page d'acceptation après clic sur lien d'invitation
export function CoupleAccept() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Token manquant"); setLoading(false); return; }
    api.get(`/couple/check/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setError("");
    setAccepting(true);
    try {
      await api.post(`/couple/accept/${token}`);
      setDone(true);
      setTimeout(() => nav("/securite"), 2000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--auprea-cream)" }}>Chargement…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div className="auprea-card max-w-md w-full">
        <Heart size={28} style={{ color: "var(--auprea-gold)", margin: "0 auto 12px", display: "block" }} />
        <h1 style={{ fontSize: 24, marginBottom: 12, textAlign: "center" }}>Invitation conjoint</h1>
        {error && !info && (
          <div style={{ color: "#b53a3a", textAlign: "center", padding: 12, fontSize: 14 }}>{error}</div>
        )}
        {info && !done && (
          <>
            <p style={{ color: "rgba(35,38,46,0.8)", fontSize: 15, lineHeight: 1.65, marginBottom: 18, textAlign: "center" }}>
              <strong>{info.inviter_name}</strong> vous invite à partager mutuellement vos carnets AUPREA en lecture seule.
            </p>
            {!user ? (
              <>
                <p style={{ fontSize: 13, color: "rgba(35,38,46,0.65)", background: "rgba(202,168,106,0.08)", padding: 12, borderRadius: 4, marginBottom: 16, lineHeight: 1.5 }}>
                  Pour accepter, connectez-vous avec le compte <strong>{info.invited_email}</strong> ou créez-le.
                </p>
                <Link to={`/connexion?next=/couple/accepter?token=${token}`} className="auprea-btn w-full inline-block text-center mb-2">Me connecter</Link>
                <Link to="/inscription" style={{ color: "var(--auprea-blue)", fontSize: 14, display: "block", textAlign: "center" }}>Créer un compte AUPREA</Link>
              </>
            ) : user.email !== info.invited_email ? (
              <div style={{ background: "#fcf3f3", border: "1px solid #f0c5c5", padding: 14, borderRadius: 4, fontSize: 14, color: "#7a2828" }}>
                Cette invitation est destinée à <strong>{info.invited_email}</strong>.
                Vous êtes actuellement connecté(e) avec <strong>{user.email}</strong>. Reconnectez-vous avec le bon compte.
              </div>
            ) : (
              <>
                {error && <div style={{ color: "#b53a3a", textAlign: "center", padding: 8, fontSize: 13, marginBottom: 10 }}>{error}</div>}
                <button onClick={accept} className="auprea-btn w-full" disabled={accepting} data-testid="couple-accept-btn">
                  {accepting ? "Acceptation…" : "Accepter l'invitation"}
                </button>
              </>
            )}
          </>
        )}
        {done && (
          <div style={{ textAlign: "center" }}>
            <Check size={28} style={{ color: "var(--auprea-green)", margin: "12px auto" }} />
            <p style={{ fontSize: 15 }}>Liaison établie ! Redirection en cours…</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Page lecture seule du carnet du conjoint
export function CarnetConjoint() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/couple/partner-carnet")
      .then((r) => setData(r.data))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Chargement…</div>;
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--auprea-cream)" }}>
        <div className="auprea-card max-w-md w-full text-center">
          <AlertTriangle size={28} style={{ color: "var(--auprea-gold)", margin: "0 auto 12px" }} />
          <p>{error}</p>
          <Link to="/securite" className="auprea-btn w-full inline-block text-center mt-4">Retour</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div style={{ background: "var(--auprea-navy)", color: "#f3efe6", padding: "12px 24px", textAlign: "center", fontSize: 13 }} data-testid="conjoint-banner">
        🔒 Carnet de votre conjoint(e) · Lecture seule
      </div>
      <div className="px-6 md:px-12 py-10 max-w-4xl mx-auto space-y-6">
        <Link to="/securite" className="flex items-center gap-2" style={{ color: "var(--auprea-blue)", fontSize: 14 }}>
          <ArrowLeft size={16} /> Retour
        </Link>
        <div className="auprea-card text-center" style={{ borderTop: "3px solid var(--auprea-gold)" }}>
          <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>— Carnet de</div>
          <h1 style={{ fontSize: 32, margin: 0 }} data-testid="partner-name">{data.partner.full_name || data.partner.email}</h1>
          <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginTop: 10 }}>
            Progression : <strong>{data.carnet?.progression || 0}%</strong>
          </div>
        </div>
        {Object.entries(data.profil || {}).map(([rubrique, fields]) => Object.keys(fields).length > 0 && (
          <div key={rubrique} className="auprea-card" data-testid={`partner-section-${rubrique}`}>
            <h2 style={{ fontSize: 20, marginBottom: 14, textTransform: "capitalize" }}>{rubrique.replace(/_/g, " ")}</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {Object.entries(fields).map(([k, v]) => v && (
                <div key={k}>
                  <dt style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(35,38,46,0.5)", fontWeight: 600, marginBottom: 3 }}>{k.replace(/_/g, " ")}</dt>
                  <dd style={{ fontSize: 14, margin: 0, whiteSpace: "pre-wrap" }}>
                    <RenderStructuredValue value={v} />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
        {(data.enfants?.length > 0 || data.biens?.length > 0 || data.personnes_confiance?.length > 0) && (
          <div className="auprea-card">
            <h2 style={{ fontSize: 20, marginBottom: 14 }}>Famille &amp; patrimoine</h2>
            {data.enfants?.length > 0 && <div className="mb-3"><strong>Enfants :</strong> {data.enfants.map((e) => `${e.prenom || ""} ${e.nom || ""}`).join(", ")}</div>}
            {data.biens?.length > 0 && <div className="mb-3"><strong>Biens immobiliers :</strong> {data.biens.length}</div>}
            {data.personnes_confiance?.length > 0 && <div><strong>Personnes de confiance :</strong> {data.personnes_confiance.map((p) => `${p.prenom || ""} ${p.nom || ""}`).join(", ")}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
